import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { motion } from 'framer-motion'
import '../../assets/css/agent-custom.css'
import loginBg from '../../assets/img/login-bg.png'
import api from '../../utils/api'
import { toast } from 'react-toastify'

export default function Login() {
    const { login } = useAuth()
    const navigate = useNavigate()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showForgotModal, setShowForgotModal] = useState(false)
    const [forgotEmail, setForgotEmail] = useState('')
    const [forgotLoading, setForgotLoading] = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)
        const result = await login(email, password)
        setLoading(false)
        if (result.success) {
            if (result.user?.role === 'admin' || result.user?.role === 'sub-admin') navigate('/admin-dashboard')
            else if (result.user?.role === 'developer') navigate('/developer-dashboard')
            else if (result.user?.role === 'agent' || result.user?.role === 'broker') navigate('/broker-dashboard')
            else if (result.user?.role === 'buyer') navigate('/buyer-dashboard')
            else if (result.user?.role === 'tenant') navigate('/tenant-dashboard')
            else if (result.user?.role === 'owner') navigate('/owner-dashboard')
            else navigate('/dashboard')
        } else {
            setError(result.message || 'Login failed. Please check your credentials.')
        }
    }

    const handleForgotSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!forgotEmail) return
        setForgotLoading(true)
        try {
            const res = await api.forgotPassword(forgotEmail)
            if (res.success) {
                toast.success('Password reset link sent to your email.')
                setShowForgotModal(false)
                setForgotEmail('')
            } else {
                toast.error(res.message || 'Failed to send reset link.')
            }
        } catch (err) {
            toast.error('Something went wrong.')
        } finally {
            setForgotLoading(false)
        }
    }

    return (
        <div className="premium-auth-wrapper">
            {/* Left Side: Image & Branding */}
            <div className="auth-image-side">
                <img src={loginBg} alt="Luxury Property" />
                <div className="auth-image-overlay">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <h1>Your Property <br />Journey Starts Here</h1>
                        <p>Login to unlock the best deals, verified listings, and expert guidance.</p>
                    </motion.div>
                </div>
            </div>

            {/* Right Side: Glassmorphism Form */}
            <div className="auth-form-side">
                <motion.div 
                    className="glass-auth-card"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="auth-logo mb-6 flex justify-center">
                        <Link to="/">
                            <img src="/white-logo.png" className="img-fluid" width="160" alt="Property Guru Logo" />
                        </Link>
                    </div>

                    <div className="premium-auth-header">
                        <motion.h2
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            Welcome Back
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                        >
                            Enter your credentials to access your portal
                        </motion.p>
                    </div>

                    {error && (
                        <motion.div 
                            className="alert alert-danger border-0 rounded-4 mb-4 small fw-bold text-center py-3 bg-danger/10 text-danger" 
                            role="alert"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <i className="fa-solid fa-circle-exclamation me-2"></i>{error}
                        </motion.div>
                    )}

                    <form onSubmit={handleSubmit} className="premium-auth-form">
                        <div className="auth-form-group">
                            <label>Email Address</label>
                            <div className="input-with-icon">
                                <i className="fa-regular fa-envelope input-icon"></i>
                                <input
                                    type="email"
                                    className="premium-auth-input shadow-none"
                                    placeholder="name@example.com"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="auth-form-group">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="mb-0">Password</label>
                                <button 
                                    type="button"
                                    onClick={() => setShowForgotModal(true)}
                                    style={{ color: '#3b82f6', background: 'none', border: 'none' }} 
                                    className="small fw-bold text-decoration-none p-0"
                                >
                                    Forgot?
                                </button>
                            </div>
                            <div className="input-with-icon">
                                <i className="fa-solid fa-lock input-icon"></i>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className="premium-auth-input shadow-none"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    required
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                            </div>
                        </div>

                        <div className="d-flex align-items-center mb-4">
                            <div className="form-check">
                                <input className="form-check-input bg-transparent border-secondary" type="checkbox" id="remember" />
                                <label className="form-check-label small text-white cursor-pointer" htmlFor="remember">
                                    Keep me signed in
                                </label>
                            </div>
                        </div>

                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            className="premium-btn-auth"
                            disabled={loading}
                        >
                            {loading ? (
                                <><span className="spinner-border spinner-border-sm me-2"></span>Signing in...</>
                            ) : (
                                <>Login <i className="fa-solid fa-arrow-right-long ms-2"></i></>
                            )}
                        </motion.button>

                        <div className="text-center mt-4 pt-2 border-t border-white/10">
                            <p className="mb-0 small text-white/60">
                                New partner? <Link to="/create-account" className="text-white fw-black text-decoration-none hover-underline ms-1 uppercase tracking-wider">Create Account</Link>
                            </p>
                        </div>
                    </form>

                </motion.div>
            </div>

            {/* Forgot Password Modal */}
            {showForgotModal && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate__animated animate__fadeIn">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        className="bg-white dark:bg-slate-900 w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
                    >
                        <div className="p-8">
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white">Forgot Password?</h3>
                                    <p className="text-slate-500 text-base font-medium mt-1">Enter your email to receive a reset link</p>
                                </div>
                                <button 
                                    onClick={() => setShowForgotModal(false)}
                                    className="text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    <i className="fa-solid fa-xmark text-xl"></i>
                                </button>
                            </div>

                            <form onSubmit={handleForgotSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <label className="text-base font-bold text-slate-700 ml-1">Email Address</label>
                                    <div className="relative">
                                        <i className="fa-regular fa-envelope absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 text-lg"></i>
                                        <input
                                            type="email"
                                            className="w-full pl-14 pr-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-lg font-bold focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                            placeholder="Enter your registered email"
                                            value={forgotEmail}
                                            onChange={e => setForgotEmail(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={forgotLoading}
                                    className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold text-base shadow-xl shadow-blue-200 transition-all active:scale-95 flex items-center justify-center gap-2"
                                >
                                    {forgotLoading ? (
                                        <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> Sending...</>
                                    ) : (
                                        <>Send Reset Link <i className="fa-solid fa-paper-plane text-xs"></i></>
                                    )}
                                </button>
                            </form>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    )
}


